// ADR-0003 D11: this is the ONLY module that imports the PocketBase SDK and
// reads `import.meta.env.VITE_PB_URL`. The sync manager takes a Transport
// interface, so production code wires the real PocketBase client and tests
// pass a fake. The `VITE_PB_URL` defaults to an empty string because in
// production the API is served same-origin under `/api/` (ADR-0005).

import PocketBase from "pocketbase";

/** Wire shape of a server-side record. */
export interface ServerRecord {
  id: string;
  /** Client clock — the value the server stored on the client's behalf. */
  updatedAt: string;
  /**
   * Server clock — used by the cursor to advance incrementally. The real
   * PocketBase response always includes this; tests and older callers may
   * omit it, in which case the syncManager falls back to `updatedAt`.
   */
  serverUpdated?: string;
  /** ISO timestamp of soft-delete, or `null` if live. */
  deletedAt: string | null;
  /** Free-form per-collection fields. */
  [field: string]: unknown;
}

/** PocketBase stores dates as `YYYY-MM-DD HH:MM:SS.mmmZ` but our local
 *  code emits and compares ISO `YYYY-MM-DDTHH:MM:SS.mmmZ`. Normalise the
 *  LWW date fields on ingress so the rest of the app sees one canonical
 *  shape (T-035 F-1). The server `updated` field is NOT normalised — it
 *  drives the incremental-pull cursor and must stay in the server's
 *  lexical format (Gate 0 D2).
 */
const SERVER_DATE_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
const NORMALISED_DATE_KEYS = new Set(["updatedAt", "deletedAt"]);

function normaliseServerDate(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (!SERVER_DATE_RE.test(value)) return value;
  return value.replace(" ", "T");
}

export function normaliseRecordDates(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(record)) {
    out[key] = NORMALISED_DATE_KEYS.has(key)
      ? normaliseServerDate(record[key])
      : record[key];
  }
  return out;
}

/** Authentication interface — kept tiny on purpose. */
export interface AuthAPI {
  isAuthenticated(): boolean;
  getOwnerId(): string;
  clear(): void;
}

/** The transport the sync engine talks to. */
export interface Transport {
  create(collection: string, record: ServerRecord): Promise<void>;
  /** ADR-0011 D2: multipart create that carries one or more file
   *  fields alongside the scalar record. Returns the created record
   *  so the engine can learn the stored filename. */
  createWithFiles(
    collection: string,
    record: ServerRecord,
    files: Record<string, Blob>,
  ): Promise<ServerRecord>;
  update(collection: string, id: string, record: ServerRecord): Promise<void>;
  /** ADR-0011 D3: multipart update — used by the retry path when the
   *  original create landed without its file. */
  updateWithFiles(
    collection: string,
    id: string,
    record: ServerRecord,
    files: Record<string, Blob>,
  ): Promise<ServerRecord>;
  softDelete(collection: string, id: string, updatedAt: string): Promise<void>;
  get(collection: string, id: string): Promise<ServerRecord | null>;
  listUpdatedSince(collection: string, cursor: string): Promise<ServerRecord[]>;
  /** T-090 F-1 + R-25: list by `(owner, exercise)` so the sync engine
   *  can recover a `create` that came back 400 with the
   *  `data.exercise`/`data.owner` shape (the live server has no
   *  upsert and the unique index returns a permanent 400). Returns
   *  at most one row under the owner rule, or 0/1+ under the coach
   *  rule. The list rule allows it (`owner = @request.auth.id ‖
   *  COACH_OF`, 2026-09-06 probe). The owner clause is sent
   *  explicitly on the wire so a coach paired with athletes does
   *  NOT get their rows — the sync engine only wants its own row,
   *  and adopting a foreign id would corrupt the local table. */
  listByExercise(
    collection: string,
    ownerId: string,
    exercise: string,
  ): Promise<ServerRecord[]>;
}

export const pbUrl: string = (import.meta.env.VITE_PB_URL ?? "") as string;

let _client: PocketBase | null = null;
function getClient(): PocketBase {
  if (_client) return _client;
  _client = new PocketBase(pbUrl || "/");
  return _client;
}

export const auth: AuthAPI = {
  isAuthenticated(): boolean {
    return getClient().authStore.isValid;
  },
  getOwnerId(): string {
    const record = getClient().authStore.record;
    if (!record) throw new Error("auth.getOwnerId(): not authenticated");
    return record.id;
  },
  clear(): void {
    getClient().authStore.clear();
  },
};

class PocketBaseTransport implements Transport {
  async create(collection: string, record: ServerRecord): Promise<void> {
    // DEF-1 fix: the client id must be sent. ADR-0003 D1 says the local
    // id is the source of truth, and the server schema accepts the
    // 15-char `[a-z0-9]+` id from the client. Destructuring id out of
    // the body forced PocketBase to autogenerate one and the local id
    // was lost on the first push.
    await getClient().collection(collection).create(record);
  }

  async createWithFiles(
    collection: string,
    record: ServerRecord,
    files: Record<string, Blob>,
  ): Promise<ServerRecord> {
    // ADR-0011 D2. PocketBase accepts multipart create; the SDK
    // detects the FormData shape and switches to multipart
    // automatically (see `isFormData` in the SDK). We name the file
    // part `<id>.webp` so the stored filename is identifiable in
    // PocketBase's admin UI even before the suffix is appended.
    const fd = recordToFormData(record, files);
    const created = await getClient().collection(collection).create(fd as unknown as ServerRecord);
    return normaliseCreatedRecord(created);
  }

  async update(collection: string, id: string, record: ServerRecord): Promise<void> {
    const { id: _ignored, ...body } = record;
    void _ignored;
    await getClient().collection(collection).update(id, body);
  }

  async updateWithFiles(
    collection: string,
    id: string,
    record: ServerRecord,
    files: Record<string, Blob>,
  ): Promise<ServerRecord> {
    // ADR-0011 D3 retry path. PocketBase update is multipart when a
    // file field is present in the body; we pass the file and the
    // scalar fields together so one round-trip re-attaches it.
    const fd = recordToFormData(record, files);
    const updated = await getClient()
      .collection(collection)
      .update(id, fd as unknown as ServerRecord);
    return normaliseCreatedRecord(updated);
  }

  async softDelete(collection: string, id: string, updatedAt: string): Promise<void> {
    await getClient().collection(collection).update(id, { deletedAt: updatedAt });
  }

  async get(collection: string, id: string): Promise<ServerRecord | null> {
    try {
      const r = await getClient().collection(collection).getOne(id);
      const rec = r as unknown as Record<string, unknown>;
      const rawUpdated = rec.updated != null ? String(rec.updated) : "";
      const normalised = normaliseRecordDates(rec);
      return {
        ...normalised,
        id: String(normalised.id),
        updatedAt: String(normalised.updatedAt ?? rawUpdated),
        serverUpdated: rawUpdated,
        deletedAt: (normalised.deletedAt as string | null | undefined) ?? null,
      } as ServerRecord;
    } catch (err) {
      if ((err as { status?: number }).status === 404) return null;
      throw err;
    }
  }

  async listByExercise(
    collection: string,
    ownerId: string,
    exercise: string,
  ): Promise<ServerRecord[]> {
    // R-25 (orchestrator, 2026-09-06): the list rule allows a coach to
    // see their paired athletes' rows (`COACH_OF`). We must filter on
    // the owner explicitly so the engine never sees a foreign row in
    // its duplicate-recovery path. The PocketBase SDK only exposes
    // `filter` as a string (pocketbase.es.d.ts:326) — no parameter
    // binding — so we interpolate. The values are local ids and
    // exercise ids, alphanumeric by construction, and PocketBase
    // filter syntax requires the inner `"` to be doubled. The
    // helper below is one line; if either value ever stops being
    // alphanumeric the helper will throw.
    const safeExercise = escapePbFilterString(exercise);
    const safeOwner = escapePbFilterString(ownerId);
    const items = await getClient()
      .collection(collection)
      .getList(1, 5, {
        filter: `exercise = "${safeExercise}" && owner = "${safeOwner}"`,
      });
    return items.items.map((r) => {
      const rec = r as unknown as Record<string, unknown>;
      const rawUpdated = rec.updated != null ? String(rec.updated) : "";
      const normalised = normaliseRecordDates(rec);
      return {
        ...normalised,
        id: String(normalised.id),
        updatedAt: String(normalised.updatedAt ?? rawUpdated),
        serverUpdated: rawUpdated,
        deletedAt: (normalised.deletedAt as string | null | undefined) ?? null,
      } as ServerRecord;
    });
  }

  async listUpdatedSince(collection: string, cursor: string): Promise<ServerRecord[]> {
    const filter = cursor ? `updated > "${cursor}"` : "";
    const items = await getClient()
      .collection(collection)
      .getList(1, 500, { filter, sort: "updated" });
    return items.items.map((r) => {
      const rec = r as unknown as Record<string, unknown>;
      // Gate 0 D2: the cursor must advance in the server's lexical format.
      // Capture the raw server `updated` value BEFORE normalising any fields.
      const rawUpdated = rec.updated != null ? String(rec.updated) : "";
      const normalised = normaliseRecordDates(rec);
      return {
        ...normalised,
        id: String(normalised.id),
        updatedAt: String(normalised.updatedAt ?? rawUpdated),
        serverUpdated: rawUpdated,
        deletedAt: (normalised.deletedAt as string | null | undefined) ?? null,
      } as ServerRecord;
    });
  }
}

/** Build a multipart body from a server record + file map. Scalars go
 *  in as plain FormData string fields (PocketBase accepts strings for
 *  any typed field except files); files go in with a deterministic
 *  name `<recordId>.<extractedFromMime>` so admin-UI filenames are
 *  identifiable. */
function recordToFormData(
  record: ServerRecord,
  files: Record<string, Blob>,
): FormData {
  const fd = new FormData();
  // id first — PocketBase respects the client id for multipart create
  // just like JSON create (ADR-0003 D1).
  fd.append("id", record.id);
  for (const [key, value] of Object.entries(record)) {
    if (key === "id" || key === "serverUpdated") continue;
    if (value == null) continue;
    fd.append(key, typeof value === "string" ? value : JSON.stringify(value));
  }
  for (const [field, blob] of Object.entries(files)) {
    const ext = extFromMime(blob.type);
    fd.append(field, blob, `${record.id}${ext}`);
  }
  return fd;
}

function extFromMime(mime: string | undefined): string {
  if (mime === "image/jpeg") return ".jpg";
  if (mime === "image/png") return ".png";
  if (mime === "image/webp") return ".webp";
  return ".bin";
}

/** PocketBase filter syntax wraps a string literal in `"`. To embed
 *  one inside another, double it (`a"b` → `a""b`). Refuse anything
 *  non-alphanumeric so a stray `"` or `\` can never escape the
 *  literal and become a clause. Our ids (15-char `[a-z0-9]+` and
 *  PocketBase relation ids) always pass; the throw is a tripwire for
 *  a future schema change. */
function escapePbFilterString(value: string): string {
  if (!/^[A-Za-z0-9]+$/.test(value)) {
    throw new Error(
      `pocketbase.filter: refusing to interpolate non-alphanumeric value: ${JSON.stringify(value)}`,
    );
  }
  return value.replace(/"/g, '""');
}

function normaliseCreatedRecord(created: unknown): ServerRecord {
  const rec = created as Record<string, unknown>;
  const rawUpdated = rec.updated != null ? String(rec.updated) : "";
  const normalised = normaliseRecordDates(rec);
  return {
    ...normalised,
    id: String(normalised.id),
    updatedAt: String(normalised.updatedAt ?? rawUpdated),
    serverUpdated: rawUpdated,
    deletedAt: (normalised.deletedAt as string | null | undefined) ?? null,
  } as ServerRecord;
}

export function createPocketBaseTransport(): Transport {
  return new PocketBaseTransport();
}
